import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Volume2, StopCircle } from 'lucide-react';
import { useSmartTTS } from '../hooks/useSmartTTS';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(229, 9, 20, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 9, 20, 0); }
`;

const wave = keyframes`
  0% { transform: scaleY(1); }
  50% { transform: scaleY(0.5); }
  100% { transform: scaleY(1); }
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TextField = styled.div`
  font-size: 24px;
  line-height: 1.6;
  color: #a3a3a3;
  font-weight: 500;
  word-break: keep-all;
  padding-bottom: 70px; /* Increased space for the floating button */
  
  @media (max-width: 768px) {
      font-size: 16px;
      padding-bottom: 30px;
  }
`;

const Word = styled.span`
  display: inline-block;
  margin-right: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.$active && `
    color: #00e0ff; /* Bright Cyan or Yellow for clear contrast */
    text-shadow: 0 0 8px rgba(0, 224, 255, 0.6);
  `}

  &:hover {
    color: #fff;
  }
`;

const FloatingButton = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  
  background: rgba(20, 20, 20, 0.6);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 50px; /* Capsule shape */
  
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);

  color: #fff;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    border-color: rgba(229, 9, 20, 0.5);
    background: rgba(229, 9, 20, 0.1);
    box-shadow: 0 4px 20px rgba(229, 9, 20, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  ${props => props.$playing && css`
    border-color: #e50914;
    color: #e50914;
    animation: ${pulse} 2s infinite;
  `}
`;

const WaveParams = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    height: 12px;
`;

const Bar = styled.div`
    width: 2px;
    height: 100%;
    background-color: #e50914;
    border-radius: 2px;
    animation: ${wave} 1s ease-in-out infinite;
    animation-delay: ${props => props.$delay}s;
`;

const KaraokeText = React.forwardRef(({ text, onWordClick, showButton = true, onStateChange, audioUrl = null }, ref) => {
    const { speak, cancel, speaking } = useSmartTTS();
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [words, setWords] = useState([]);

    useEffect(() => {
        if (text) setWords(text.split(/\s+/));
    }, [text]);

    useEffect(() => {
        if (onStateChange) onStateChange(speaking);
    }, [speaking, onStateChange]);

    const handlePlay = () => {
        if (speaking) {
            cancel();
            setHighlightIndex(-1);
            return;
        }

        setHighlightIndex(-1);
        const totalChars = text.length;

        speak({
            text,
            audioUrl,
            type: 'sentence',
            onProgress: (currentTime, duration) => {
                const lookahead = 0.2;
                const adjustedTime = Math.min(currentTime + lookahead, duration);
                const progress = adjustedTime / duration;
                const currentChar = Math.floor(progress * totalChars);

                let charCount = 0;
                let foundIndex = -1;
                for (let i = 0; i < words.length; i++) {
                    charCount += words[i].length + 1;
                    if (charCount > currentChar) {
                        foundIndex = i;
                        break;
                    }
                }
                if (foundIndex !== -1) setHighlightIndex(foundIndex);
            },
            onEnd: () => setHighlightIndex(-1)
        });
    };

    // Expose methods to parent
    React.useImperativeHandle(ref, () => ({
        handlePlay
    }));

    return (
        <Container>
            <TextField>
                {words.map((word, index) => (
                    <Word
                        key={index}
                        $active={Math.abs(index - highlightIndex) <= 1}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (onWordClick) {
                                onWordClick(word, index);
                            } else {
                                handlePlay();
                            }
                        }}
                    >
                        {word}
                    </Word>
                ))}
            </TextField>

            {showButton && (
                <FloatingButton
                    onClick={(e) => { e.stopPropagation(); handlePlay(); }}
                    $playing={speaking}
                >
                    {speaking ? (
                        <>
                            <StopCircle size={18} />
                            <span>Stop</span>
                            <WaveParams>
                                <Bar $delay={0} />
                                <Bar $delay={0.2} />
                                <Bar $delay={0.4} />
                            </WaveParams>
                        </>
                    ) : (
                        <>
                            <Volume2 size={18} />
                            <span>Listen</span>
                        </>
                    )}
                </FloatingButton>
            )}
        </Container>
    );
});

export default KaraokeText;
